import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { PromoteDialog } from '@/features/tracker/PromoteDialog'
import { useDemoteTicket, extractErrorMessage } from '@/queries/ticketMutations'
import { useIsAdmin } from '@/lib/auth'
import { trackerStatusLabels, trackerStatusVariants } from '@/lib/status'
import type { Ticket } from '@/types/ticket'

// Staff-only, like promote/demote on the server: checked here (not just by
// the caller's route guard) so "Track this issue" is verifiably absent for a
// non-admin regardless of where this ever ends up being rendered.
export function TrackerControls({ ticket }: { ticket: Ticket }) {
  const isAdmin = useIsAdmin()
  const demote = useDemoteTicket(ticket._id)

  if (!isAdmin) return null

  // One full-width button in the rail's action stack, matching its siblings.
  // The tracker status it currently carries is worth showing, so a promoted
  // ticket gets the badge above the button rather than losing it.
  if (!ticket.tracker) {
    return (
      <PromoteDialog
        ticketId={ticket._id}
        companyId={ticket.companyId}
        trigger={<Button variant="secondary" className="w-full">Track this issue</Button>}
      />
    )
  }

  return (
    <div className="flex flex-col gap-2">
      <Badge variant={trackerStatusVariants[ticket.tracker.status]} className="self-start">
        {trackerStatusLabels[ticket.tracker.status]}
      </Badge>
      <Button
        variant="secondary"
        disabled={demote.isPending}
        onClick={async () => {
          if (!confirm('Stop tracking this issue? It will no longer be visible to the client company.')) return
          try {
            await demote.mutateAsync()
            toast.success('Tracking stopped')
          } catch (err) {
            toast.error(extractErrorMessage(err, 'Could not stop tracking'))
          }
        }}
      >
        Stop tracking
      </Button>
    </div>
  )
}
